<template>
	<div class="p-fluid p-formgrid p-grid p-input-filled">
		<div class="p-field p-col-12">
			<label for="setting">{{$t('Squeeze.Locators.Name')}} *</label>
			<Dropdown id="setting"  style="min-width: 15.625rem" v-model="value.setting" :options="supportedLocatorSettings" :class="{'p-invalid':v$.setting.$invalid && showErrorMessage}" @change="update" :disabled="disableSetting" :showClear="true" :filter="false" optionValue="setting" optionLabel="setting" :editable="false" />
			<small v-if="v$.setting.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.Locators.Name')}) }}
			</small>
		</div>
		<div class="p-field p-col-12">
			<label for="value">{{$t('Squeeze.Locators.Value')}} *</label>
			<InputText id="value" v-model.trim="value.value" :class="{'p-invalid':v$.value.$invalid && showErrorMessage}" @change="update" required="true" />
			<small v-if="v$.value.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.Locators.Value')}) }}
			</small>
		</div>
	</div>
</template>

<script lang="ts">
import {defineComponent, nextTick, onMounted, PropType, reactive, watch} from "vue";
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import InputText from "primevue/inputtext";
import {LocatorSetting} from "@dex/squeeze-client-ts";
import Dropdown from "primevue/dropdown";
import {useI18n} from "vue-i18n";
import {FormState} from "@/util/Types";

type LocatorSettingFormState = FormState<LocatorSetting, "setting" | "value">;

export default defineComponent({
	name: 'LocatorAttributeForm',
	components: {
		InputText,
		Dropdown,
	},
	props: {
		fieldEntry: {
			type: Object as PropType<LocatorSetting>,
			default: () => ({}),
		},
		/** All Valid Settings */
		supportedLocatorSettings: {
			type: Array as PropType<LocatorSetting[]>,
			default: () => [],
		},
		showErrorMessage: {
			type: Boolean,
			default: false,
		},
		disableSetting: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['update'],
	setup(props, {emit}) {
		const {t} = useI18n();

		/** Current Object of all input-fields */
		const value = reactive<LocatorSettingFormState>({locatorId: props.fieldEntry.locatorId, setting: '', value: ''});

		/** Determines the required rules for validation */
		const rules = {
			setting: { required },
			value: { required },
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value);

		onMounted(async () => {
			Object.assign(value, props.fieldEntry);

			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(() => props.fieldEntry, () => {
			Object.assign(value, props.fieldEntry);
		})

		/** Triggered on change of any field */
		const update = async () => {
			// need nextTick, because the dropdown don't check the change of a selected element (is to slow)
			await nextTick();
			emit("update", value, v$.value.$invalid);
		}

		return {
			t,
			value,
			props,
			v$,
			InputText,
			update,
		}
	},
});
</script>

<style scoped>

</style>

